<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { DatePicker } from 'v-calendar'
import * as z from 'zod'
import { toTypedSchema } from '@vee-validate/zod'
import { HOST, HOST_TULUM, API } from '@/utils/http'
import { isClosedLineString, closedLineStringToPolygon } from '@/utils/geometry'

import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'

import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

const props = defineProps({
  feature: {
    type: Object,
    required: true
  },
  orgId: {
    type: String,
    required: true
  },
  name: {
    type: String,
    required: true
  }
})

const emit = defineEmits(['formSubmitted'])

const store = useStore()

const hasPlanetscopeAccess = computed(() => store.getters['geo/getIsPlanetscopeAccess'])

// Dynamic types fetched from API
const types = ref([])
const isLoading = ref(true)
const loadError = ref(null)

const selectedType = ref(null)
const selectedVariableId = ref(null)

const selectedTypeData = computed(() => {
  return types.value.find(type => type.value === selectedType.value)
})

const selectedIds = computed(() => {
  return selectedTypeData.value?.ids || []
})

// Find the currently selected resource object (with start_time/end_time)
const selectedResourceData = computed(() => {
  return selectedIds.value.find(id => id.value === selectedVariableId.value)
})

// Parse "YYYY-MM-DD" to local Date (no timezone shift)
const parseDateString = (str) => {
  if (!str) return null
  const [y, m, d] = str.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const minDate = computed(() => parseDateString(selectedResourceData.value?.start_time))
const maxDate = computed(() => {
  const parsed = parseDateString(selectedResourceData.value?.end_time)
  return parsed || new Date()
})

const disabledDates = computed(() => {
  const rules = []
  if (minDate.value) {
    rules.push({ start: null, end: new Date(minDate.value.getFullYear(), minDate.value.getMonth(), minDate.value.getDate() - 1) })
  }
  if (maxDate.value) {
    rules.push({ start: new Date(maxDate.value.getFullYear(), maxDate.value.getMonth(), maxDate.value.getDate() + 1), end: null })
  }
  return rules
})

// When type changes, select first variable
watch(selectedType, () => {
  if (selectedIds.value.length > 0) {
    selectedVariableId.value = selectedIds.value[0].value
  }
})


// Fetch available Planetary Variables from API
const fetchVariables = async () => {
  isLoading.value = true
  loadError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST_TULUM}/planet/${props.orgId}/variables`
    })

    const groups = response.data.groups || []

    // Transform API response to match component's expected format
    types.value = groups.map(group => ({
      value: group.id,
      label: group.label,
      ids: group.resources.map(r => ({
        value: r.resource_id,
        label: r.label,
        start_time: r.start_time || null,
        end_time: r.end_time || null
      }))
    }))

    // Set default selection to first type and first variable
    if (types.value.length > 0) {
      selectedType.value = types.value[0].value
      if (types.value[0].ids?.length > 0) {
        selectedVariableId.value = types.value[0].ids[0].value
      }
    }
  } catch (error) {
    console.error('Failed to fetch Planetary Variables:', error)
    loadError.value = extractErrorMessage(error)
    // Fallback to empty state
    types.value = []
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchVariables()
})

/**
 * Extract error message from API error response
 * @param {Error} error - The error object from API request
 * @returns {string} - Formatted error message
 */
const extractErrorMessage = (error) => {
  let errorMessage = error.message

  if (error.response?.data) {
    const errorData = error.response.data

    if (errorData.detail) {
      // Handle FastAPI HTTPException detail
      errorMessage = typeof errorData.detail === 'string'
        ? errorData.detail
        : JSON.stringify(errorData.detail)
    } else if (errorData.error) {
      // Handle custom error format
      const details = errorData.details || []
      const detailsStr = Array.isArray(details) ? details.join(', ') : details

      let errorText = ''
      if (typeof errorData.error === 'object' && errorData.error !== null && !Array.isArray(errorData.error)) {
        // Safely access nested properties using optional chaining
        errorText = errorData.error?.reason || errorData.error?.message || JSON.stringify(errorData.error)
      } else if (typeof errorData.error === 'string') {
        errorText = errorData.error
      } else {
        // Handle arrays or other complex types
        errorText = JSON.stringify(errorData.error)
      }
      errorMessage = `${errorText}: ${detailsStr}`
    }
  }

  return errorMessage
}

const formSchema = toTypedSchema(z.object({
  layerType: z.string(),
  layerId: z.string(),
  start_date: z.date(),
  end_date: z.date()
}).refine(data => {
  return data.start_date <= data.end_date
}, {
  message: 'Start date must be before or equal to end date',
  path: ['start_date']
}))

// Default start to the last year of availability instead of the variable's
// full history — full-range orders are heavy and rarely intended.
const defaultStartDate = computed(() => {
  if (!maxDate.value) return minDate.value || undefined
  const yearBefore = new Date(maxDate.value.getFullYear() - 1, maxDate.value.getMonth(), maxDate.value.getDate())
  if (minDate.value && yearBefore < minDate.value) return minDate.value
  return yearBefore
})

const initialValues = computed(() => ({
  layerType: selectedType.value,
  layerId: selectedVariableId.value,
  start_date: defaultStartDate.value,
  end_date: maxDate.value || undefined
}))

// Format Date to UTC midnight string without timezone shift
const toUTCDateString = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}T00:00:00Z`
}

// Format Date for display without timezone shift
const formatLocalDate = (date) => {
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

const onSubmit = async (values) => {
  const layer = {
    org_id: parseInt(props.orgId),
    type: 2, // Subscription type
    name: props.name,
    layer: {
      aoi: isClosedLineString(props.feature.geometry)
        ? closedLineStringToPolygon(props.feature.geometry)
        : props.feature.geometry,
      data: {
        type: values.layerType,
        parameters: {
          id: values.layerId,
          start_date: toUTCDateString(values.start_date),
          end_date: toUTCDateString(values.end_date)
        }
      }
    }
  }

  console.log(layer)

  try {
    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${props.orgId}/layers`,
      data: layer
    })

    toast({ title: 'Layer created successfully' })
    emit('formSubmitted', response.data)

  } catch (error) {
    console.error(error)

    const errorMessage = extractErrorMessage(error)

    toast({
      title: 'Error creating layer',
      description: errorMessage,
      variant: 'destructive'
    })
  }
}
</script>

<template>
<div class="space-y-4">
  <!-- Loading state -->
  <div v-if="isLoading" class="flex items-center justify-center py-8">
    <Spinner class="w-5 h-5 mr-2" />
    <span class="text-muted-foreground">Loading available variables...</span>
  </div>

  <!-- Error state -->
  <div v-else-if="loadError" class="text-red-500 py-4">
    <p class="font-medium">Failed to load Planetary Variables</p>
    <p class="text-sm">{{ loadError }}</p>
    <Button variant="outline" size="sm" class="mt-2" @click="fetchVariables">
      Retry
    </Button>
  </div>

  <!-- No variables available -->
  <div v-else-if="types.length === 0" class="text-muted-foreground py-4">
    <p>No Planetary Variables available for this organization.</p>
    <p class="text-sm">Please check your Planet subscription includes Planetary Variables access.</p>
  </div>

  <!-- Form (when data is loaded) - key forces re-render when type changes -->
  <Form v-else class="space-y-3" :key="`${selectedType}-${selectedVariableId}`" :validation-schema="formSchema" :initial-values="initialValues" @submit="onSubmit">
  <FormField v-slot="{ componentField }" name="layerType">
    <FormItem>
      <FormLabel>Type</FormLabel>
      <FormControl>
        <Select v-bind="componentField" v-model="selectedType">
          <SelectTrigger>
            <SelectValue placeholder="Source type" />
          </SelectTrigger>
          <SelectContent class="max-h-[300px]">
            <SelectGroup>
              <SelectItem
                v-for="(type, index) in types"
                :key="index"
                :value="type.value"
              >
                {{ type.label }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-slot="{ componentField }" name="layerId">
    <FormItem>
      <FormLabel>Variable</FormLabel>
      <FormControl>
        <Select v-bind="componentField" v-model="selectedVariableId">
          <SelectTrigger>
            <SelectValue placeholder="Select variable" />
          </SelectTrigger>
          <SelectContent class="max-h-[300px]">
            <SelectGroup>
              <SelectItem
                v-for="(id, index) in selectedIds"
                :key="index"
                :value="id.value"
              >
                {{ id.label }}
              </SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-slot="{ componentField }" name="start_date">
    <FormItem>
      <FormLabel>Start date</FormLabel>
      <Popover>
        <PopoverTrigger>
          <Badge variant="secondary">
            {{ componentField.modelValue ? formatLocalDate(componentField.modelValue) : 'Select date' }}
          </Badge>
        </PopoverTrigger>
        <PopoverContent class="p-0 w-full">
          <DatePicker v-bind="componentField" :disabled-dates="disabledDates" />
        </PopoverContent>
      </Popover>
      <FormMessage />
    </FormItem>
  </FormField>

  <FormField v-slot="{ componentField }" name="end_date" >
    <FormItem>
      <FormLabel>End date</FormLabel>
      <Popover>
        <PopoverTrigger>
          <Badge variant="secondary">
            {{ componentField.modelValue ? formatLocalDate(componentField.modelValue) : 'Select date' }}
          </Badge>
        </PopoverTrigger>
        <PopoverContent class="p-0 w-full">
          <DatePicker v-bind="componentField" :disabled-dates="disabledDates" />
        </PopoverContent>
      </Popover>
      <FormMessage />
    </FormItem>
  </FormField>

  <div class="mt-4 flex justify-end">
    <Button type="submit">Add</Button>
  </div>
</Form>
</div>
</template>
